/**
 * buildEvidencePackage — creates the canonical EvidencePackage per ply.
 *
 * This is the heart of the analysis pipeline: it converts raw engine
 * output + CommentEngine heuristics into a structured, coach-ready object
 * that never invents data.
 */
import { Chess } from 'chess.js';
import { classifyMove } from './classifiers/moveClassifier';
import { detectOpening } from '../data/openings-detector';
import { checkMaterialLoss } from './heuristics/materialHeuristic';
import { checkHangingPiece } from './heuristics/hangingPieceHeuristic';
import { checkKingSafety } from './heuristics/kingSafetyHeuristic';
import { checkDevelopment } from './heuristics/developmentHeuristic';
import { checkCenterControl } from './heuristics/centerControlHeuristic';
import { checkPawnStructure } from './heuristics/pawnStructureHeuristic';
import { checkEndgameTransition } from './heuristics/endgameTransitionHeuristic';
// ── Helpers ──────────────────────────────────────────────────────────────────
function cpToEvalPoint(cpPawns) {
    return { type: 'cp', value: Math.round((cpPawns ?? 0) * 100) };
}
function mateToEvalPoint(mate) {
    return { type: 'mate', value: mate };
}
function heuristicToMotif(h) {
    const hints = {
        loses_major_material: 'Has perdido una pieza de alto valor (Torre o Dama).',
        loses_minor_material: 'Has perdido material (Peón, Caballo o Alfil).',
        hangs_piece: 'Has dejado una pieza sin protección que puede ser capturada.',
        king_unsafe: 'Has debilitado la estructura de peones que protege a tu rey.',
        development_slow: 'Has movido la misma pieza varias veces en la apertura, retrasando tu desarrollo.',
        allows_mate: '¡Cuidado! Esta jugada permite un jaque mate inevitable.',
        is_checkmate: '¡Jaque Mate! La partida ha terminado.',
        stalemate: 'La partida ha terminado en tablas por ahogado.',
        check_threat: 'Tu rey está bajo ataque directo (¡Jaque!).',
        missed_tactic: 'Había una oportunidad táctica mejor que has pasado por alto.',
        center_control: 'Has mejorado tu influencia sobre las casillas centrales.',
        pawn_structure: 'Tu estructura de peones se ha visto comprometida.',
        endgame_transition: 'La partida entra en una fase de finales: los reyes se vuelven piezas activas.',
    };
    return {
        tag: h.id,
        severity: h.severity,
        hint: hints[h.id] ?? h.id.replace(/_/g, ' '),
        data: h.data,
    };
}
function buildArrows(moveUci, bestMoveUci, motifs, pvUci, fenBefore) {
    const arrows = [];
    const highlights = [];
    const playedFrom = moveUci.slice(0, 2);
    const playedTo = moveUci.slice(2, 4);
    let bestFrom = bestMoveUci?.slice(0, 2);
    let bestTo = bestMoveUci?.slice(2, 4);
    // Green arrow for the played move
    arrows.push({ from: playedFrom, to: playedTo, color: 'rgba(99, 215, 130, 0.75)' });
    // Validate if the bestMove is actually legal in this position
    const chess = new Chess(fenBefore);
    let isBestLegal = false;
    if (bestMoveUci && bestMoveUci.length >= 4) {
        try {
            const moveResult = chess.move({
                from: bestFrom,
                to: bestTo,
                promotion: bestMoveUci[4]
            });
            if (moveResult) {
                isBestLegal = true;
                // Use standardized move from result just in case
                bestFrom = moveResult.from;
                bestTo = moveResult.to;
            }
        }
        catch (e) {
            isBestLegal = false;
        }
    }
    const isBest = isBestLegal && moveUci.slice(0, 4) === bestMoveUci.slice(0, 4);
    if (!isBest && isBestLegal) {
        arrows.push({ from: bestFrom, to: bestTo, color: 'rgba(255, 170, 30, 0.75)' });
    }
    // Highlight squares for motifs
    for (const m of motifs) {
        if (m.data?.square)
            highlights.push(m.data.square);
    }
    // Highlight 1st PV response move destination
    if (pvUci.length > 1) {
        highlights.push(pvUci[1].slice(2, 4));
    }
    return {
        suggestedArrows: arrows,
        suggestedHighlights: highlights,
        playedMove: { from: playedFrom, to: playedTo },
        bestMove: isBest ? undefined : { from: bestFrom, to: bestTo },
    };
}
export function buildEvidencePackage(input) {
    const { ply, fenBefore, fenAfter, moveUci, moveSan, evalBeforePawns, evalAfterPawns, isMate, mateMoves, mateBefore, bestMoveUci, engineLines = [], engineDepth = 15, uciHistory = [], playerColor, } = input;
    const chess = new Chess(fenBefore);
    // sideThatMoved = whose turn it was BEFORE the move (they are the one who made the move)
    const sideThatMoved = chess.turn();
    // ── Eval points for UI display (white-relative, as-is from engine) ─────
    const evalBefore = cpToEvalPoint(evalBeforePawns);
    const evalAfter = isMate && mateMoves !== undefined && mateMoves !== null
        ? mateToEvalPoint(mateMoves)
        : cpToEvalPoint(evalAfterPawns);
    // ── White-relative delta ───────────────────────────────────────────────
    // rawDelta > 0 means white improved, rawDelta < 0 means black improved
    const rawDeltaWhite = (evalAfterPawns !== null && evalBeforePawns !== null)
        ? evalAfterPawns - evalBeforePawns
        : null;
    // deltaCpForMover: from the perspective of who just moved
    // White moved → positive rawDelta = good for them → keep sign
    // Black moved → positive rawDelta = good for WHITE = BAD for black → flip
    const deltaCpForMover = rawDeltaWhite !== null
        ? (sideThatMoved === 'w' ? rawDeltaWhite * 100 : -rawDeltaWhite * 100)
        : 0;
    // ── Normalized inputs for classifyMove — ALL from mover's perspective ──
    // classifyMove expects: positive = mover is winning, negative = mover is losing
    const normalizedEvalBefore = evalBeforePawns !== null
        ? (sideThatMoved === 'w' ? evalBeforePawns : -evalBeforePawns)
        : null;
    const normalizedEvalAfter = evalAfterPawns !== null
        ? (sideThatMoved === 'w' ? evalAfterPawns : -evalAfterPawns)
        : null;
    const normalizedDelta = rawDeltaWhite !== null
        ? (sideThatMoved === 'w' ? rawDeltaWhite : -rawDeltaWhite)
        : null;
    const chessAfter = new Chess(fenAfter);
    // mateAfter: from mover's perspective (positive = mover delivers mate)
    // mateMoves from engine is already white-relative after parseEngineInfo conversion
    // We need it from the mover's perspective for classifyMove
    const mateAfterRaw = isMate ? (mateMoves ?? null) : (chessAfter.isCheckmate() ? 0 : null);
    const normalizedMateAfter = mateAfterRaw !== null
        ? (sideThatMoved === 'w' ? mateAfterRaw : -mateAfterRaw)
        : null;
    // mateBefore from mover's perspective
    const normalizedMateBefore = mateBefore !== null && mateBefore !== undefined
        ? (sideThatMoved === 'w' ? mateBefore : -mateBefore)
        : null;
    const evalInput = {
        evalBefore: normalizedEvalBefore,
        evalAfter: normalizedEvalAfter,
        delta: normalizedDelta,
        isMate,
        mateBefore: normalizedMateBefore,
        mateAfter: normalizedMateAfter,
        bestMove: bestMoveUci,
    };
    const rawLabel = (evalAfterPawns === null || (engineDepth !== undefined && engineDepth < 8 && !isMate))
        ? 'analysing'
        : classifyMove(evalInput);
    // Book move detection - ensure we use full history
    const opening = detectOpening([...uciHistory, moveUci]);
    const isBookMove = !!opening && rawLabel !== 'blunder' && rawLabel !== 'mistake';
    const finalLabel = isBookMove ? 'book' : rawLabel;
    // ── Engine lines ──────────────────────────────────────────────────
    const lines = engineLines.map(l => ({
        multipv: l.multipv,
        score: { type: 'cp', value: l.scoreCp },
        pvUci: l.pvUci.slice(0, 5),
        depth: l.depth,
    }));
    const primaryPv = lines.find(l => l.multipv === 1)?.pvUci ?? [];
    // ── Motif hints configuration ──────────────────────────────────
    // humanMoved = true when the human player (playerColor) is the one who just moved
    const humanMoved = playerColor ? sideThatMoved === playerColor : true;
    const hints = {
        loses_major_material: humanMoved
            ? 'Has perdido una pieza de alto valor (Torre o Dama).'
            : 'El rival ha perdido una pieza de alto valor.',
        loses_minor_material: humanMoved
            ? 'Has perdido material (Peón, Caballo o Alfil).'
            : 'El rival ha perdido material.',
        hangs_piece: humanMoved
            ? 'Has dejado una pieza sin protección que puede ser capturada.'
            : 'El rival ha dejado una pieza sin protección.',
        king_unsafe: humanMoved
            ? 'Has debilitado la estructura de peones que protege a tu rey.'
            : 'El rival ha debilitado la protección de su rey.',
        development_slow: humanMoved
            ? 'Has movido la misma pieza varias veces en la apertura, retrasando tu desarrollo.'
            : 'El rival está retrasando su desarrollo.',
        checkmate_loss: humanMoved
            ? '¡Cuidado! Esta jugada permite un jaque mate inevitable.'
            : 'El rival ha cometido un error que permite un jaque mate.',
        is_checkmate: humanMoved
            ? '¡Jaque Mate! ¡Has ganado la partida!'
            : '¡Jaque Mate! Has sido derrotado.',
        stalemate: 'La partida ha terminado en tablas por ahogado.',
        check_threat: humanMoved
            ? 'Has puesto al rey rival en jaque.'
            : 'El rival te ha puesto en jaque.',
        missed_tactic: humanMoved
            ? 'Había una oportunidad táctica mejor que has pasado por alto.'
            : 'El rival no ha encontrado la mejor jugada.',
        capture: humanMoved
            ? 'Has capturado material del oponente.'
            : 'El rival ha capturado una de tus piezas.',
        center_control: humanMoved
            ? 'Has tomado el control de casillas clave en el centro.'
            : 'El rival está ganando espacio en el centro.',
        pawn_structure: humanMoved
            ? 'Cuidado con la estructura de peones: has creado debilidades.'
            : 'El rival ha comprometido su estructura de peones.',
        endgame_transition: 'La partida está llegando al final. Simplificación y planes a largo plazo.',
    };
    const motifs = [];
    // Capture detection
    if (moveSan.includes('x')) {
        motifs.push({ tag: 'capture', severity: 'low', hint: hints.capture });
    }
    // ── King safety & Game state heuristics ──────────────────────────
    if (chessAfter.isCheckmate()) {
        motifs.push({ tag: 'is_checkmate', severity: 'high', hint: hints.is_checkmate });
    }
    else {
        if (chessAfter.inCheck()) {
            motifs.push({ tag: 'check_threat', severity: 'medium', hint: hints.check_threat });
        }
        if (chessAfter.isStalemate()) {
            motifs.push({ tag: 'stalemate', severity: 'medium', hint: hints.stalemate });
        }
        // Logic for allowing mate (if next move results in mate for the mover)
        if (isMate && mateMoves !== null && mateMoves !== undefined) {
            const isOpponentMate = (sideThatMoved === 'w' && mateMoves < 0) || (sideThatMoved === 'b' && mateMoves > 0);
            if (isOpponentMate) {
                motifs.push({ tag: 'allows_mate', severity: 'high', hint: hints.allows_mate });
            }
        }
    }
    const rawHeuristics = [
        checkMaterialLoss(fenBefore, fenAfter),
        checkHangingPiece(fenBefore, fenAfter),
        checkKingSafety(fenBefore, fenAfter),
        checkDevelopment(fenBefore, fenAfter),
        checkCenterControl(fenBefore, fenAfter),
        checkPawnStructure(fenBefore, fenAfter),
        checkEndgameTransition(fenBefore, fenAfter),
    ].filter(Boolean);
    rawHeuristics.forEach(h => {
        if (h)
            motifs.push(heuristicToMotif(h));
    });
    // ── If the move was a blunder/mistake, add a tactical motif ──────
    if ((finalLabel === 'blunder' || finalLabel === 'mistake') && primaryPv.length > 0 && motifs.length === 0) {
        motifs.push({
            tag: 'missed_tactic',
            severity: finalLabel === 'blunder' ? 'high' : 'medium',
            hint: `El motor prefería ${bestMoveUci.slice(0, 2)}→${bestMoveUci.slice(2, 4)} que habría mantenido la ventaja.`,
        });
    }
    // ── UI (arrows + highlights) ──────────────────────────────────────
    const ui = buildArrows(moveUci, bestMoveUci, motifs, primaryPv, fenBefore);
    return {
        ply,
        fenBefore,
        fenAfter,
        sideThatMoved,
        moveSan,
        moveUci,
        engine: {
            depth: engineDepth,
            evalBefore,
            evalAfter,
            deltaCpForMover: Math.round(deltaCpForMover),
            bestMoveUci,
            lines,
        },
        quality: {
            label: finalLabel,
            lossCp: Math.round(Math.max(0, -deltaCpForMover)),
        },
        opening: opening ? {
            name: opening.name,
            group: opening.groupName,
            inBook: isBookMove,
            commentary: opening.commentary,
        } : undefined,
        motifs,
        ui,
    };
}
